import React from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { SeasonTotals } from './types';
import { OUTCOME } from './dataViz';

interface Props {
  data: SeasonTotals;
}

const COLORS = [OUTCOME.win, OUTCOME.loss];

// Granularity coarse → fine: a team match contains several lines, each line is
// played as sets, each set as games. Shown side by side so the whole picture
// reads at a glance without a toggle.
const UNITS: Array<{
  key: string;
  label: string;
  won: keyof SeasonTotals;
  lost: keyof SeasonTotals;
}> = [
  { key: 'matches', label: 'Matches', won: 'matches_won', lost: 'matches_lost' },
  { key: 'lines', label: 'Lines', won: 'lines_won', lost: 'lines_lost' },
  { key: 'sets', label: 'Sets', won: 'sets_won', lost: 'sets_lost' },
  { key: 'games', label: 'Games', won: 'games_won', lost: 'games_lost' },
];

const MiniDonut: React.FC<{ label: string; won: number; lost: number; testId: string }> = ({
  label,
  won,
  lost,
  testId,
}) => {
  const total = won + lost;
  return (
    <div data-testid={testId} className="flex flex-col items-center">
      <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">{label}</span>
      {total === 0 ? (
        <div className="h-28 w-28 flex items-center justify-center">
          <span className="text-xs text-gray-400 dark:text-gray-500 italic">No data</span>
        </div>
      ) : (
        <div className="relative h-28 w-28">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={[
                  { name: `${label} won`, value: won },
                  { name: `${label} lost`, value: lost },
                ]}
                innerRadius={34}
                outerRadius={50}
                paddingAngle={2}
                dataKey="value"
                stroke="none"
              >
                <Cell fill={COLORS[0]} />
                <Cell fill={COLORS[1]} />
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {Math.round((won / total) * 100)}%
            </span>
          </div>
        </div>
      )}
      <span className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">{`${won}–${lost}`}</span>
    </div>
  );
};

const SeasonDonut: React.FC<Props> = ({ data }) => (
  <div data-testid="season-donut" className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-2">
    {UNITS.map((u) => (
      <MiniDonut
        key={u.key}
        testId={`season-donut-${u.key}`}
        label={u.label}
        won={data[u.won]}
        lost={data[u.lost]}
      />
    ))}
  </div>
);

export default SeasonDonut;
